import axios from "axios";

const BASE_URL = (import.meta.env.VITE_API_URL || "http://localhost:3000") + "/api";

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryableStatusCodes: [500, 502, 503, 504], // Server errors
};

// Sanitize error message for security
const sanitizeErrorMessage = (error: any): string => {
  // If it's a server error (5xx), show generic message
  if (error.response?.status >= 500) {
    return "Something went wrong. Please try again.";
  }
  
  // If it's a client error (4xx), show the error message if it's safe
  if (error.response?.status >= 400 && error.response?.status < 500) {
    const errorMessage = error.response?.data?.error;
    
    // Only show specific error messages for safe client errors
    if (error.response?.status === 400 && errorMessage) {
      return errorMessage;
    }
    
    if (error.response?.status === 404) {
      return "Resource not found.";
    }
    
    if (error.response?.status === 403) {
      return "Access denied.";
    }
    
    if (error.response?.status === 401) {
      return "Authentication required.";
    }
  }
  
  // Default generic message for any other errors
  return "Something went wrong. Please try again.";
};

// Retry function
const retryRequest = async (error: any, retryCount: number = 0): Promise<any> => {
  const { config } = error;
  
  if (!config || retryCount >= RETRY_CONFIG.maxRetries) {
    throw error;
  }
  
  const shouldRetry = RETRY_CONFIG.retryableStatusCodes.includes(error.response?.status);
  
  if (!shouldRetry) {
    throw error;
  }
  
  // Exponential backoff
  const delay = RETRY_CONFIG.retryDelay * Math.pow(2, retryCount);
  
  console.log(`Retrying request (${retryCount + 1}/${RETRY_CONFIG.maxRetries}) after ${delay}ms`);
  
  await new Promise(resolve => setTimeout(resolve, delay));
  
  try {
    return await axios(config);
  } catch (retryError) {
    return retryRequest(retryError, retryCount + 1);
  }
};

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Important for sending cookies
});

api.interceptors.request.use(
  (config) => {
    // Add Firebase token or custom token to Authorization header
    const firebaseToken = localStorage.getItem("firebaseToken");
    const customToken = localStorage.getItem("customToken");
    
    if (firebaseToken) {
      config.headers.Authorization = `Bearer ${firebaseToken}`;
    } else if (customToken) {
      config.headers.Authorization = `Bearer ${customToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If token is invalid, redirect to login
    if (error.response?.status === 401) {
      console.error("Authentication failed, redirecting to login");
      localStorage.removeItem("firebaseToken");
      localStorage.removeItem("customToken");
      localStorage.removeItem("firebaseUser");
      localStorage.removeItem("userContext");
      window.location.href = "/login";
      return Promise.reject(error);
    }
    
    // Try to retry the request for server errors
    if (RETRY_CONFIG.retryableStatusCodes.includes(error.response?.status)) {
      try {
        return await retryRequest(error);
      } catch (retryError: any) {
        // If retry fails, sanitize the error message
        const sanitizedError = new Error(sanitizeErrorMessage(retryError));
        sanitizedError.name = retryError.name;
        sanitizedError.stack = retryError.stack;
        return Promise.reject(sanitizedError);
      }
    }
    
    // For non-retryable errors, sanitize the error message
    const sanitizedError = new Error(sanitizeErrorMessage(error));
    sanitizedError.name = error.name;
    sanitizedError.stack = error.stack;
    return Promise.reject(sanitizedError);
  }
);

export default api;
